import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  Select, MenuItem, FormControl, InputLabel, Tooltip, Divider,
  useMediaQuery,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import ScienceIcon from '@mui/icons-material/Science';
import { useSelector } from 'react-redux';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import { matchStoreSearch } from '../../utils/matchStoreSearch';
import SignaturePad from 'signature_pad';

const STATUS_MAP = {
  none: { label: '미발송', color: 'default' },
  sent: { label: '발송됨', color: 'info' },
  signed: { label: '서명 완료', color: 'success' },
  expired: { label: '만료', color: 'error' },
  renewal_pending: { label: '갱신 대기', color: 'warning' },
};

export default function ContractSettings() {
  const { user } = useSelector(s => s.User);
  /** Redux user 객체는 액션마다 새 참조가 될 수 있음 → [user] 의존 시 fetchStores 반복 → 테이블 로딩 플리커 */
  const userId = useMemo(() => user?.user?._id || user?._id || '', [user?._id, user?.user?._id]);
  const isCompact = useMediaQuery('(max-width:1280px)');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // 상태 변경 다이얼로그
  const [editStore, setEditStore] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editSignee, setEditSignee] = useState('');
  const [saving, setSaving] = useState(false);

  // 업로드 다이얼로그
  const [uploadStore, setUploadStore] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // 이력 다이얼로그
  const [logStore, setLogStore] = useState(null);
  const [logs, setLogs] = useState([]);

  const fetchStores = useCallback(async (opts = {}) => {
    const { withSpinner = true } = opts;
    if (!userId) return;
    if (withSpinner) setLoading(true);
    try {
      const { data } = await api.get(`/store/getall/${userId}`);
      const list = data?.stores || data || [];
      setStores(Array.isArray(list) ? list : []);
    } catch (e) { console.error('[ContractSettings] fetchStores failed:', e.response?.status, e.response?.data?.message || e.message); setStores([]); }
    finally { if (withSpinner) setLoading(false); }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchStores({ withSpinner: true });
  }, [userId, fetchStores]);

  const filtered = stores.filter((s) => matchStoreSearch(s, search));

  // 통계
  const stats = {
    total: stores.length,
    none: stores.filter(s => !s.contractStatus || s.contractStatus === 'none').length,
    sent: stores.filter(s => s.contractStatus === 'sent').length,
    signed: stores.filter(s => s.contractStatus === 'signed').length,
    expired: stores.filter(s => s.contractStatus === 'expired').length,
  };

  const actionBaseBtnSx = {
    fontSize: 11,
    py: 0.45,
    px: 0.9,
    minWidth: 0,
    minHeight: 28,
    borderRadius: 1.2,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  };
  const actionSolidSx = {
    ...actionBaseBtnSx,
    background: '#551FFF',
    '&:hover': { background: '#4318e0' },
  };
  const actionOutlineSx = {
    ...actionBaseBtnSx,
    borderColor: '#cfd4dc',
    color: '#334155',
    '&:hover': { borderColor: '#94a3b8', background: '#f8fafc' },
  };
  const actionSuccessSx = {
    ...actionBaseBtnSx,
    borderColor: '#22c55e',
    color: '#16a34a',
    '&:hover': { borderColor: '#16a34a', background: '#f0fdf4' },
  };

  // 상태 변경
  const openEditDialog = (store) => {
    setEditStore(store);
    setEditStatus(store.contractStatus || 'none');
    setEditNote('');
    setEditSignee(store.contractSigneeName || store.ownerName || '');
  };

  const saveStatus = async () => {
    if (!editStore) return;
    setSaving(true);
    try {
      await api.patch(`/store/contract-status/${editStore._id}`, {
        status: editStatus,
        note: editNote,
        signeeName: editSignee,
      });
      toast.success('상태 변경 완료');
      setEditStore(null);
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('변경 실패: ' + (e.response?.data?.message || e.message));
    }
    setSaving(false);
  };

  // 계약서 생성/발송 준비
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDialogLoading, setSendDialogLoading] = useState(false);
  const [sendStore, setSendStore] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sendOwnerPhone, setSendOwnerPhone] = useState('');
  const [sendClientName, setSendClientName] = useState('');
  const [sendStoreName, setSendStoreName] = useState('');
  const [sendClientLine, setSendClientLine] = useState('');
  const [sendContractDate, setSendContractDate] = useState(() => new Date().toISOString().slice(0, 10));

  /** 계약서 Client는 점주 이름만 사용 — 매장명(store.name)은 넣지 않음 */
  const buildDefaultClientLine = (store, ownerNameInput) => {
    const cityLine = [store?.town, store?.state, store?.zipcode].filter(Boolean).join(', ');
    const addr = [store?.address1, cityLine].filter(Boolean).join(', ');
    const name = (ownerNameInput ?? store?.ownerName ?? '').trim();
    if (!name && !addr) return '';
    if (!name) return `${addr} ("Client")`;
    return addr ? `${name}, ${addr} ("Client")` : `${name} ("Client")`;
  };

  const closeSendDialog = () => {
    setSendDialogOpen(false);
    setSendStore(null);
  };

  const openSendDialog = async (store) => {
    setSendDialogOpen(true);
    setSendDialogLoading(true);
    const baseOwnerName = (store.ownerName || '').trim();
    setSendStore(store);
    setSendEmail(store.ownerEmail || '');
    setSendOwnerPhone(store.ownerPhone || '');
    setSendClientName(baseOwnerName);
    setSendStoreName(store.name || '');
    setSendClientLine(buildDefaultClientLine(store, baseOwnerName));
    setSendContractDate(new Date().toISOString().slice(0, 10));
    try {
      const { data } = await api.get(`/store/get/${store._id}`);
      const latestStore = data?.store || data || store;
      const ownerOnly = (latestStore.ownerName || '').trim();
      setSendStore(latestStore);
      setSendEmail(latestStore.ownerEmail || '');
      setSendOwnerPhone(latestStore.ownerPhone || '');
      setSendClientName(ownerOnly);
      setSendStoreName(latestStore.name || '');
      setSendClientLine(buildDefaultClientLine(latestStore, ownerOnly));
    } catch (_) {}
    setSendDialogLoading(false);
  };

  const generateContractOnly = async () => {
    if (!sendStore) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/store/generate-contract/${sendStore._id}`, {
        clientName: sendClientName,
        storeName: sendStoreName,
        clientLine: sendClientLine,
        email: sendEmail,
        ownerPhone: sendOwnerPhone,
        contractDate: sendContractDate,
      });
      toast.success(
        data?.contactUpdated?.length
          ? `계약서 생성 완료 · 스토어에 ${data.contactUpdated.join(', ')} 저장됨`
          : '계약서 생성 완료',
      );
      if (data.pdfUrl) window.open(data.pdfUrl, '_blank');
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('생성 실패: ' + (e.response?.data?.message || e.message));
    }
    setGenerating(false);
  };

  const doSend = async () => {
    if (!sendStore || !sendEmail) return;
    setSending(true);
    try {
      const { data } = await api.post(`/store/contract-send/${sendStore._id}`, {
        email: sendEmail,
        ownerPhone: sendOwnerPhone,
        clientName: sendClientName,
        storeName: sendStoreName,
        clientLine: sendClientLine,
        contractDate: sendContractDate,
      });
      toast.success(
        data?.contactUpdated?.length
          ? `계약서 발송 완료: ${sendEmail} · 스토어 연락처 저장됨`
          : `계약서 발송 완료: ${sendEmail}`,
      );
      closeSendDialog();
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('발송 실패: ' + (e.response?.data?.message || e.message));
    }
    setSending(false);
  };

  // 드라이 테스트 (이메일 없이 서명 URL 직접 오픈)
  const [dryTesting, setDryTesting] = useState(false);
  const doDryTest = async () => {
    if (!sendStore) return;
    setDryTesting(true);
    try {
      const { data } = await api.post(`/store/contract-send/${sendStore._id}`, {
        email: sendEmail || sendStore.ownerEmail || 'test@test.com',
        ownerPhone: sendOwnerPhone,
        clientName: sendClientName,
        storeName: sendStoreName,
        clientLine: sendClientLine,
        contractDate: sendContractDate,
        dryTest: true,
      });
      toast.success('드라이 테스트: 미리보기만 생성 (매장·링크 변경 없음)');
      if (data.signUrl) window.open(data.signUrl, '_blank');
      if (data.pdfUrl) window.open(data.pdfUrl, '_blank');
      closeSendDialog();
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('드라이 테스트 실패: ' + (e.response?.data?.message || e.message));
    }
    setDryTesting(false);
  };

  // 카운터서명
  const [counterSignStore, setCounterSignStore] = useState(null);
  const [counterSignerName, setCounterSignerName] = useState('Minjung Lee');
  const [counterSigning, setCounterSigning] = useState(false);
  const counterSigCanvasRef = useRef(null);
  const counterSigPadRef = useRef(null);

  const openCounterSignDialog = (store) => {
    setCounterSignStore(store);
    setCounterSignerName('Minjung Lee');
    setTimeout(() => {
      const canvas = counterSigCanvasRef.current;
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      if (counterSigPadRef.current) counterSigPadRef.current.off();
      counterSigPadRef.current = new SignaturePad(canvas, {
        minWidth: 1.5, maxWidth: 3.5, penColor: '#1a1a2e',
        backgroundColor: 'rgba(0,0,0,0)', velocityFilterWeight: 0.7,
      });
    }, 200);
  };

  const doCounterSign = async () => {
    if (!counterSignStore) return;
    if (!counterSigPadRef.current || counterSigPadRef.current.isEmpty()) {
      return toast.error('서명을 입력해주세요');
    }
    setCounterSigning(true);
    try {
      const signatureImage = counterSigPadRef.current.toDataURL('image/png');
      const { data } = await api.post(`/store/counter-sign/${counterSignStore._id}`, {
        signerName: counterSignerName,
        signatureImage,
      });
      toast.success(data.message || '카운터서명 완료');
      setCounterSignStore(null);
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('카운터서명 실패: ' + (e.response?.data?.message || e.message));
    }
    setCounterSigning(false);
  };

  // 파일 업로드
  const openUploadDialog = (store) => {
    setUploadStore(store);
    setUploadFile(null);
  };

  const doUpload = async () => {
    if (!uploadStore || !uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('contract', uploadFile);
      const { data } = await api.post(`/store/contract-upload/${uploadStore._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('업로드 완료: ' + data.fileName);
      setUploadStore(null);
      fetchStores({ withSpinner: false });
    } catch (e) {
      toast.error('업로드 실패: ' + (e.response?.data?.message || e.message));
    }
    setUploading(false);
  };

  // 이력 조회
  const openLogs = async (store) => {
    setLogStore(store);
    try {
      const { data } = await api.get(`/store/contract-logs/${store._id}`);
      setLogs(data.logs || []);
    } catch (_) { setLogs([]); }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>
          <DescriptionIcon sx={{ fontSize: { xs: 17, sm: 18 }, verticalAlign: 'middle', mr: 0.5 }} />
          계약서 관리
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end', maxWidth: { xs: '100%', sm: 'none' } }}>
          {Object.entries(STATUS_MAP).map(([key, { label, color }]) => (
            <Chip key={key} label={`${label}: ${stats[key] || 0}`} size="small" color={color}
              variant="outlined" sx={{ fontSize: { xs: 9, sm: 10 }, fontWeight: 600, height: { xs: 22, sm: 24 } }} />
          ))}
        </Box>
      </Box>

      {/* 검색 */}
      {/* 워크플로우 안내 */}
      <Alert severity="info" sx={{ mb: 1.2, '& .MuiAlert-message': { fontSize: { xs: 10, sm: 11 }, lineHeight: 1.6 } }}>
        <strong>Adobe Acrobat Sign 워크플로우:</strong>
        &nbsp; 생성 → Acrobat Pro에서 서명 요청 → 매장주 서명 → 서명 완료 PDF 업로드 → 상태 변경
      </Alert>
      <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: { xs: 10, sm: 11 }, lineHeight: 1.5 } }}>
        현재 화면은 <strong>수동 Adobe Sign 워크플로우</strong>입니다. Adobe eSign 계정이 있으면 사용 가능하며,
        API 자동화(Agreement 생성/웹훅 상태동기화)는 별도 OAuth/Integration Key 연동 작업이 필요합니다.
      </Alert>

      {/* 검색 */}
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 0 }}>
            <SearchIcon sx={{ color: '#888', fontSize: 20, flexShrink: 0 }} />
            <TextField
              fullWidth
              size="small"
              placeholder="점포명, TLJ No, 오너, 주소, 상태 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontSize: { xs: 13, sm: 13 } } }}
            />
          </Box>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchStores({ withSpinner: true })}
            sx={{ whiteSpace: 'nowrap', fontSize: 11, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            새로고침
          </Button>
        </Box>
      </Paper>

      {/* 테이블 */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 2,
            maxHeight: { xs: 'min(calc(100vh - 340px), 70vh)', sm: 'min(calc(100vh - 300px), 75vh)', md: 560 },
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: { xs: 720, sm: 750 } }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: '#888', background: '#fafafa', whiteSpace: 'nowrap' } }}>
                <TableCell>Store</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>서명인</TableCell>
                <TableCell>서명일</TableCell>
                <TableCell>계약서</TableCell>
                <TableCell align="center">액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((s) => {
                const st = STATUS_MAP[s.contractStatus] || STATUS_MAP.none;
                return (
                  <TableRow key={s._id} hover sx={{
                    background: s.contractStatus === 'signed' ? 'rgba(34,197,94,0.03)' :
                      s.contractStatus === 'expired' ? 'rgba(231,76,60,0.03)' : 'white',
                  }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-word' }}>
                      {s.name || s.tljNo || '—'}
                      {s.name && s.tljNo && s.name !== s.tljNo && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 10, color: '#888', fontWeight: 400 }}>
                          {s.tljNo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#555', wordBreak: 'break-word' }}>{s.ownerName || '-'}</TableCell>
                    <TableCell>
                      <Chip label={st.label} size="small" color={st.color}
                        variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#555' }}>
                      {s.contractSigneeName || '-'}
                      {s.counterSignedBy && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 9, color: '#d97706', fontWeight: 600 }}>
                          CS: {s.counterSignedBy}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 10, color: '#555' }}>
                      {s.contractSignedAt ? new Date(s.contractSignedAt).toLocaleDateString('ko-KR') : '-'}
                    </TableCell>
                    <TableCell>
                      {s.contractDocUrl ? (
                        <Button size="small" variant="text" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          href={s.contractDocUrl} target="_blank"
                          sx={{ fontSize: 10, py: 0, textTransform: 'none', color: '#551FFF' }}>
                          보기
                        </Button>
                      ) : <Typography sx={{ fontSize: 10, color: '#ccc' }}>없음</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'inline-grid', gridTemplateColumns: `repeat(2, minmax(0, ${isCompact ? 84 : 96}px))`, gap: 0.7 }}>
                        <Button size="small" variant="contained" fullWidth
                          startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                          onClick={() => openSendDialog(s)}
                          sx={actionSolidSx}>
                          생성
                        </Button>
                        <Button size="small" variant="outlined" fullWidth
                          startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                          onClick={() => openUploadDialog(s)}
                          sx={actionSuccessSx}>
                          업로드
                        </Button>
                        <Button size="small" variant="outlined" fullWidth
                          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                          onClick={() => openEditDialog(s)}
                          sx={actionOutlineSx}>
                          상태
                        </Button>
                        {s.contractStatus === 'signed' && !s.counterSignedBy ? (
                          <Button size="small" variant="outlined" fullWidth
                            startIcon={<BorderColorIcon sx={{ fontSize: 14 }} />}
                            onClick={() => openCounterSignDialog(s)}
                            sx={{ ...actionBaseBtnSx, borderColor: '#f59e0b', color: '#d97706', '&:hover': { borderColor: '#d97706', background: '#fffbeb' } }}>
                            카운터서명
                          </Button>
                        ) : (
                          <Button size="small" variant="text" fullWidth
                            startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                            onClick={() => openLogs(s)}
                            sx={{ ...actionBaseBtnSx, color: '#64748b', '&:hover': { background: '#f8fafc', color: '#334155' } }}>
                            이력
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#888', fontSize: 13 }}>
                    {stores.length === 0 ? '등록된 매장이 없습니다.' : '검색 결과 없음'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ═══ 상태 변경 Dialog ═══ */}
      <Dialog open={!!editStore} onClose={() => setEditStore(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF' }}>
          계약 상태 변경 — {editStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: 12 }}>상태</InputLabel>
            <Select value={editStatus} label="상태" onChange={(e) => setEditStatus(e.target.value)}
              sx={{ fontSize: 13 }}>
              <MenuItem value="none">미발송</MenuItem>
              <MenuItem value="sent">발송됨</MenuItem>
              <MenuItem value="signed">서명 완료</MenuItem>
              <MenuItem value="expired">만료</MenuItem>
              <MenuItem value="renewal_pending">갱신 대기</MenuItem>
            </Select>
          </FormControl>
          {editStatus === 'signed' && (
            <TextField fullWidth size="small" label="서명인 이름" value={editSignee}
              onChange={(e) => setEditSignee(e.target.value)} sx={{ mb: 2, '& input': { fontSize: 13 } }} />
          )}
          <TextField fullWidth size="small" label="메모 (선택)" value={editNote}
            onChange={(e) => setEditNote(e.target.value)} multiline rows={2}
            sx={{ '& textarea': { fontSize: 12 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditStore(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={saveStatus} disabled={saving}
            sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
            {saving ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 업로드 Dialog ═══ */}
      <Dialog open={!!uploadStore} onClose={() => setUploadStore(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF' }}>
          계약서 업로드 — {uploadStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          {uploadStore?.contractDocUrl && (
            <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
              기존 파일: <a href={uploadStore.contractDocUrl} target="_blank" rel="noreferrer">{uploadStore.contractDocUrl}</a>
              <br />새 파일 업로드 시 덮어씁니다.
            </Alert>
          )}
          <Button variant="outlined" component="label" fullWidth
            startIcon={<UploadFileIcon />}
            sx={{ py: 2, fontSize: 13, borderStyle: 'dashed', borderColor: '#551FFF', color: '#551FFF' }}>
            {uploadFile ? uploadFile.name : 'PDF / 이미지 파일 선택'}
            <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
          </Button>
          {uploadFile && (
            <Typography sx={{ mt: 1, fontSize: 11, color: '#888' }}>
              {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadStore(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={doUpload} disabled={uploading || !uploadFile}
            sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
            {uploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 이력 Dialog ═══ */}
      <Dialog open={!!logStore} onClose={() => setLogStore(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: '#551FFF' }} />
          계약 이력 — {logStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* 현재 상태 요약 */}
          {logStore && (
            <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2, mt: 1, background: '#f8f7ff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: 12 }}>
                    상태: <Chip label={STATUS_MAP[logStore.contractStatus]?.label || '미발송'} size="small"
                      color={STATUS_MAP[logStore.contractStatus]?.color || 'default'} sx={{ fontSize: 10, height: 20, ml: 0.5 }} />
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5 }}>
                    서명인: {logStore.contractSigneeName || '-'} | 서명일: {logStore.contractSignedAt ? new Date(logStore.contractSignedAt).toLocaleDateString('ko-KR') : '-'}
                  </Typography>
                </Box>
                {logStore.contractDocUrl && (
                  <Button size="small" variant="outlined" href={logStore.contractDocUrl} target="_blank"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: 10, borderColor: '#551FFF', color: '#551FFF' }}>
                    계약서 보기
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {/* 이력 타임라인 */}
          {logs.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: 'center', color: '#888', fontSize: 13 }}>
              이력이 없습니다.
            </Typography>
          ) : (
            <Box>
              {logs.map((log, i) => (
                <Box key={log._id} sx={{ display: 'flex', gap: 1.5, py: 1, borderLeft: `2px solid ${i === 0 ? '#551FFF' : '#e5e7eb'}`, pl: 1.5, ml: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? '#551FFF' : '#ddd', mt: 0.5, flexShrink: 0, ml: -1.8 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: i === 0 ? 700 : 400 }}>
                        {log.action === 'uploaded' ? '파일 업로드' :
                         log.action === 'status_changed' ? '상태 변경' :
                         log.action === 'signed' ? '서명 완료' :
                         log.action === 'sent' ? '발송' :
                         log.action === 'created' ? '생성' : log.action}
                        {log.fromStatus && log.toStatus && (
                          <span style={{ color: '#888', fontWeight: 400 }}> ({STATUS_MAP[log.fromStatus]?.label} → {STATUS_MAP[log.toStatus]?.label})</span>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#888' }}>
                        {new Date(log.createdAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </Typography>
                    </Box>
                    {log.note && (
                      <Typography sx={{ fontSize: 11, color: '#555', mt: 0.3, whiteSpace: 'pre-line', lineHeight: 1.5,
                        background: '#f8fafc', borderRadius: 1, px: 1, py: 0.5 }}>
                        {log.note}
                      </Typography>
                    )}
                    {log.fileUrl && (
                      <Button size="small" variant="text" href={log.fileUrl} target="_blank"
                        sx={{ fontSize: 10, p: 0, mt: 0.2, color: '#551FFF' }}>
                        {log.fileName || '파일 보기'}
                      </Button>
                    )}
                    {log.user && (
                      <Typography sx={{ fontSize: 10, color: '#aaa', mt: 0.2 }}>by {log.user.username || log.user.email}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogStore(null)} sx={{ color: '#888' }}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 발송 Dialog ═══ */}
      <Dialog open={sendDialogOpen} onClose={closeSendDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #1565C0' }}>
          <SendIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle', color: '#1565C0' }} />
          계약서 생성/발송 — {sendStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          {sendDialogLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>스토어 최신 정보 불러오는 중...</Typography>
            </Box>
          )}
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, mb: 1.2, background: '#f8fafc' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#334155', mb: 0.4 }}>DB 확인 정보</Typography>
            <Typography sx={{ fontSize: 11, color: '#475569' }}>
              Owner: {sendStore?.ownerName || '-'} | Email: {sendStore?.ownerEmail || '-'} | Phone: {sendStore?.ownerPhone || '-'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.2 }}>
              Address: {[sendStore?.address1, sendStore?.town, sendStore?.state, sendStore?.zipcode].filter(Boolean).join(', ') || '-'}
            </Typography>
          </Paper>
          <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
            생성 전에 점주 이름·매장명·연락처를 확인하고 필요 시 수정하세요.
            DB에 오너·이메일·전화가 비어 있으면, 입력한 값이 스토어에 자동 저장됩니다.
            하단에서 &quot;생성만&quot; 또는 &quot;검토 후 발송&quot;을 선택할 수 있습니다.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="점주 이름"
              value={sendClientName}
              onChange={(e) => {
                const v = e.target.value;
                setSendClientName(v);
                if (sendStore) setSendClientLine(buildDefaultClientLine(sendStore, v));
              }}
              placeholder="계약서상 Client(점주) 실명"
              sx={{ '& input': { fontSize: 13 } }}
            />
            <TextField fullWidth size="small" label="Store name" value={sendStoreName}
              onChange={(e) => setSendStoreName(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField fullWidth size="small" label="Client line (계약서 표시 문구)"
              value={sendClientLine}
              onChange={(e) => setSendClientLine(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }}
              helperText='예: WA_Terry, 101 Terry Ave N, Seattle, WA, 98109 ("Client")' />
            <TextField size="small" label="계약 날짜" type="date" value={sendContractDate || ''}
              onChange={(e) => setSendContractDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, '& input': { fontSize: 13 } }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField fullWidth size="small" label="수신 이메일" value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }}
              placeholder="store.owner@email.com" />
            <TextField fullWidth size="small" label="Owner 전화 번호 확인" value={sendOwnerPhone}
              onChange={(e) => setSendOwnerPhone(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }}
              placeholder="(xxx) xxx-xxxx" />
          </Stack>
          <Typography sx={{ fontSize: 10, color: '#888' }}>
            Store: {sendStore?.name} | Owner: {sendStore?.ownerName || '-'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={closeSendDialog} sx={{ color: '#888' }}>취소</Button>
          <Button variant="outlined" onClick={doDryTest} disabled={dryTesting}
            startIcon={dryTesting ? <CircularProgress size={14} /> : <ScienceIcon />}
            sx={{ borderColor: '#f59e0b', color: '#d97706', '&:hover': { borderColor: '#d97706', background: '#fffbeb' } }}>
            {dryTesting ? '테스트 중...' : '드라이 테스트'}
          </Button>
          <Button variant="outlined" onClick={generateContractOnly} disabled={generating}
            startIcon={generating ? <CircularProgress size={14} /> : <DescriptionIcon />}
            sx={{ borderColor: '#551FFF', color: '#551FFF' }}>
            {generating ? '생성 중...' : '생성만'}
          </Button>
          <Button variant="contained" onClick={doSend} disabled={sending || !sendEmail}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
            sx={{ background: '#1565C0', '&:hover': { background: '#0d47a1' } }}>
            {sending ? '발송 중...' : '검토 후 발송'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 카운터서명 Dialog ═══ */}
      <Dialog open={!!counterSignStore} onClose={() => setCounterSignStore(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #f59e0b' }}>
          <BorderColorIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle', color: '#d97706' }} />
          카운터서명 (Provider) — {counterSignStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          {counterSignStore?.contractDocUrl && (
            <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
              서명 완료 계약서: <a href={counterSignStore.contractDocUrl} target="_blank" rel="noreferrer">PDF 보기</a>
              <br />고객 서명인: {counterSignStore.contractSigneeName || '-'} | 서명일: {counterSignStore.contractSignedAt ? new Date(counterSignStore.contractSignedAt).toLocaleDateString('ko-KR') : '-'}
            </Alert>
          )}
          <TextField fullWidth size="small" label="서명자 이름" value={counterSignerName}
            onChange={(e) => setCounterSignerName(e.target.value)}
            sx={{ mb: 2, '& input': { fontSize: 13 } }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Provider 서명</Typography>
          <Box sx={{ border: '2px dashed #d97706', borderRadius: 2, position: 'relative', mb: 1, background: '#fffef5' }}>
            <canvas ref={counterSigCanvasRef} style={{ width: '100%', height: 150, display: 'block', touchAction: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" variant="text" onClick={() => counterSigPadRef.current?.clear()}
              sx={{ fontSize: 11, color: '#888' }}>Clear</Button>
            <Button size="small" variant="text" onClick={() => {
              if (!counterSigPadRef.current) return;
              const d = counterSigPadRef.current.toData();
              if (d.length > 0) { d.pop(); counterSigPadRef.current.fromData(d); }
            }} sx={{ fontSize: 11, color: '#888' }}>Undo</Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCounterSignStore(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={doCounterSign} disabled={counterSigning}
            startIcon={counterSigning ? <CircularProgress size={14} /> : <BorderColorIcon />}
            sx={{ background: '#d97706', '&:hover': { background: '#b45309' } }}>
            {counterSigning ? '서명 중...' : '카운터서명 완료'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
